import { IPatient } from '../interfaces'
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { Col, Divider, Form, Row, notification } from 'antd'
import {
  Checkbox,
  Disabled,
  Input,
  Select,
  UploadImageCrop
} from 'components/atoms'
import { FooterModal, SelectAddress } from 'components/templates'
import { JOB_OPTIONS, MARITAL_STATUS_OPTIONS } from 'constants/options'
import { PLACE_HOLDER_EMPTY } from 'constants/placeholder'
import { EUserRole } from 'enums/EUserRole'
import { useModal, useOptionConstant } from 'hooks'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import { useReceiveServices } from 'services'
import styled from 'styled-components'
import { StyledLabel } from 'styles/styledGlobal'
import {
  formatDate,
  getDayOptions,
  getMonthOptions,
  getYearBirthdayOptions
} from 'utils'
import { EMAIL_REG, PHONE_NUMBER_REG } from 'utils/regularExpressions'

const StyledCheckBox = styled(Checkbox)`
  color: var(--color-primary-108);
  .ant-checkbox-inner {
    border-color: var(--color-primary-108);
  }
`
const StyledCheckBoxGuardian = styled(Checkbox)`
  .ant-checkbox-inner {
    border-color: var(--color-primary-108);
  }
  padding-bottom: 2%;
`
const StyledForm = styled(Form)`
  height: calc(100vh - 160px);
  overflow: auto;
  overflow-x: hidden;
  padding: 24px;
  .street-house {
    margin-top: 0px;
  }
  .ant-upload {
    height: 100px;
  }
`

const enum KeyForm {
  FullName = 'full_name',
  PhoneNumber = 'phone_number',
  Email = 'email',
  Gender = 'gender',
  Password = 'password',
  DateOfBirth = 'date_of_birth',
  Academic = 'academic',
  Religion = 'religion',
  CitizenId = 'citizen_id',
  Job = 'job',
  Address = 'address',
  Nation = 'nation',
  AvatarUrl = 'avatar_url',
  MaritalStatus = 'marital_status',
  Year = 'year',
  Month = 'month',
  Date = 'date',
  Guardian = 'guardian',
  GuardianName = 'guardian_name',
  GuardianYearBirthday = 'guardian_year_birthday',
  GuardianPhoneNumber = 'guardian_phone_number'
}

interface IProps {
  patient?: IPatient
}

const AddProfileModal = (props: IProps) => {
  const { patient } = props
  const [form] = Form.useForm()
  const modal = useModal()
  const [validateTrigger, setValidateTrigger] = useState({
    phoneNumber: ['onBlur'],
    email: ['onBlur']
  })
  const { createPatient, updatePatientInfo } = useReceiveServices()
  const [isCreateAccount, setIsCreateAccount] = useState(true)
  const [needGuardian, setNeedGuardian] = useState(false)
  const [cannotUncheck, setCannotUncheck] = useState(false)
  const [loading, setLoading] = useState(false)
  const { genderOptions, countryOptions } = useOptionConstant()
  const [passwordVisible, setPasswordVisible] = useState(true)
  const [reset, setRest] = useState(false)
  const isEdit = !!patient

  const handleOnSubmit = async () => {
    setValidateTrigger({
      phoneNumber: ['onBlur', 'onChange'],
      email: ['onBlur', 'onChange']
    })
    const fields = await form.validateFields()
    setLoading(true)
    const apiCreateUpdate = isEdit ? updatePatientInfo : createPatient
    const year = fields?.year ? Number(fields?.year) : 0
    const date = fields?.date ? Number(fields?.date) : 0
    const month = fields?.month ? Number(fields?.month) : 0
    const query = {
      ...fields,
      address: fields?.address?.filter((x: any) => Boolean(x)).join(', '),
      role: EUserRole.Patient,
      city_name: fields?.address?.[3],
      district_name: fields?.address?.[2],
      ward_name: fields?.address?.[1],
      street_number: fields?.address?.[0],
      id: patient?.user_id,
      email: fields?.email,
      marital_status: fields?.marital_status,
      year,
      date,
      month,
      guardian: {
        guardian_name: fields?.guardian_name || '',
        guardian_year_birthday: fields?.guardian_year_birthday || 0,
        guardian_phone_number: fields?.guardian_phone_number || ''
      }
    }
    apiCreateUpdate(query)
      .then((res) => {
        if (res._successfully) {
          form.resetFields()
          const message = isEdit
            ? 'edit-patient-success'
            : 'add-new-patient-success'
          notification.success({ message: t(message) })
          modal.close(res?.data)
        }
      })
      .finally(() => setLoading(false))
  }

  const initValue = {
    [KeyForm.Nation]: 'vn',
    [KeyForm.Password]: '1900633426'
  }

  useEffect(() => {
    if (patient?.user_id) {
      console.log('patient change')
      form.setFieldsValue({
        [KeyForm.FullName]: patient.full_name,
        [KeyForm.Email]: patient.email,
        [KeyForm.PhoneNumber]: patient.phone_number,
        [KeyForm.Gender]: patient.gender || undefined,
        [KeyForm.CitizenId]: patient.citizen_id,
        [KeyForm.Academic]: patient.academic,
        [KeyForm.Job]: patient.job,
        [KeyForm.Nation]: patient.nation || 'vn',
        [KeyForm.Religion]: patient.religion,
        [KeyForm.AvatarUrl]: patient.avatar_url,
        [KeyForm.MaritalStatus]: patient.marital_status || undefined,
        [KeyForm.Address]: [
          patient.street_number,
          patient.ward_name,
          patient.district_name,
          patient.city_name
        ],
        [KeyForm.Date]: patient?.date ? Number(patient.date) : undefined,
        [KeyForm.Month]: patient?.month ? Number(patient.month) : undefined,
        [KeyForm.Year]: patient?.year ? Number(patient.year) : undefined,
        [KeyForm.GuardianName]: patient?.guardian?.guardian_name,
        [KeyForm.GuardianPhoneNumber]: patient?.guardian?.guardian_phone_number,
        [KeyForm.GuardianYearBirthday]: patient?.guardian
          ?.guardian_year_birthday
          ? Number(patient?.guardian?.guardian_year_birthday)
          : undefined
      })
    }
    if (
      patient?.guardian?.guardian_name ||
      patient?.guardian?.guardian_phone_number ||
      patient?.guardian?.guardian_year_birthday
    )
      setNeedGuardian(true)
  }, [patient?.user_id])
  const month = form?.getFieldValue('month')
  const date = form?.getFieldValue('date')
  const year = form?.getFieldValue('year')

  useEffect(() => {
    const guardianBirthday = new Date()
    const patientBirthday = new Date(
      form?.getFieldValue('year'),
      form?.getFieldValue('month') - 1 || 0,
      form?.getFieldValue('date') || 1
    )
    const ageDifference =
      guardianBirthday.getFullYear() - patientBirthday.getFullYear()
    const monthDifference =
      guardianBirthday.getMonth() - patientBirthday.getMonth()
    const dayDifference = guardianBirthday.getDate() - patientBirthday.getDate()
    let age = ageDifference
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--
    }

    if (age < 18) {
      setNeedGuardian(true)
      setCannotUncheck(true)
    } else {
      setCannotUncheck(false)
    }
  }, [
    form?.getFieldValue('year'),
    form?.getFieldValue('month'),
    form?.getFieldValue('date')
  ])
  useEffect(() => {
    const element = document.getElementById('add-profile-modal')
    if (!!element?.scrollTop) element.scrollTo(0, Number(element?.clientHeight))
  }, [isCreateAccount])

  const handleSetPasswordVisible = (show: boolean) => {
    setPasswordVisible(show)
    setTimeout(() => {
      const pivot = form.getFieldValue(KeyForm.Password)?.length
      const inputElement = document.getElementById('add-patient-password')
      if (inputElement) {
        inputElement.focus()
        ;(inputElement as any).setSelectionRange(pivot, pivot)
      }
    })
  }

  return (
    <Disabled disabled={loading}>
      <StyledForm
        form={form}
        autoComplete="off"
        id="add-profile-modal"
        validateTrigger={['onChange', 'onBlur']}
        initialValues={initValue}
        onFieldsChange={() => setRest(!reset)}>
        <StyledLabel>
          {t('created-at')}:{' '}
          {formatDate(patient?.created_at) || formatDate(new Date())}
        </StyledLabel>
        <Divider plain>{t('administrative-information').toUpperCase()}</Divider>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={KeyForm.FullName}
              rules={[
                {
                  required: true,
                  message: t('please-enter-info') || ''
                },
                {
                  max: 100,
                  message:
                    t('name-not-100-chart', {
                      name: t('patient').toLowerCase()
                    }) || ''
                }
              ]}>
              <Input
                maxLength={100}
                isCapitalizeName
                title={t('fullname')}
                required
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={KeyForm.Gender}
              rules={[
                {
                  required: true,
                  message: t('please-enter-info') || ''
                }
              ]}>
              <Select title={t('gender')} required options={genderOptions} />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item name={KeyForm.Date}>
              <Select
                allowClear
                showSearch
                title={t('day')}
                optionFilterProp="children"
                options={getDayOptions(month, year)}
                notFoundContent={<></>}
              />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item name={KeyForm.Month}>
              <Select
                allowClear
                showSearch
                title={t('month')}
                optionFilterProp="children"
                options={getMonthOptions(date, year)}
                notFoundContent={<></>}
              />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item
              name={KeyForm.Year}
              rules={[
                {
                  required: true,
                  message: t('please-enter-info') || ''
                }
              ]}>
              <Select
                required
                showSearch
                title={t('birthday')}
                optionFilterProp="children"
                options={getYearBirthdayOptions(120, month, date)}
                onChange={(val) => {
                  const currentYear = new Date().getFullYear()
                  if (currentYear - val < 18) {
                    setNeedGuardian(true)
                    setCannotUncheck(true)
                  } else setCannotUncheck(false)
                }}
                notFoundContent={<></>}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={KeyForm.PhoneNumber}
              validateTrigger={validateTrigger.phoneNumber}
              rules={[
                {
                  required: true,
                  message: t('please-enter-info') || ''
                },
                {
                  pattern: PHONE_NUMBER_REG,
                  message: t('format-number-error') || ''
                }
              ]}>
              <Input
                type="number"
                title={t('phone-number')}
                required
                onBlur={() =>
                  setValidateTrigger({
                    ...validateTrigger,
                    phoneNumber: ['onBlur', 'onChange']
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={KeyForm.Email}
              validateTrigger={validateTrigger.email}
              rules={[
                {
                  pattern: EMAIL_REG,
                  message: t('format-email-error') || ''
                }
              ]}>
              <Input
                title={t('email')}
                onBlur={() =>
                  setValidateTrigger({
                    ...validateTrigger,
                    email: ['onBlur', 'onChange']
                  })
                }
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={KeyForm.CitizenId}>
              <Input title={t('citizen-identification')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={KeyForm.MaritalStatus}>
              <Select
                title={t('marital-status')}
                options={MARITAL_STATUS_OPTIONS}
                placeholder={PLACE_HOLDER_EMPTY}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name={KeyForm.Job}>
              <Select
                title={t('job')}
                options={JOB_OPTIONS}
                placeholder={PLACE_HOLDER_EMPTY}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider plain>{t('address-info').toUpperCase()}</Divider>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name={KeyForm.Address}>
              <SelectAddress
                spans={[8, 8, 8, 16]}
                countryComponent={
                  <Col span={8}>
                    <Form.Item name={KeyForm.Nation}>
                      <Select
                        title={t('country')}
                        required
                        options={countryOptions}
                      />
                    </Form.Item>
                  </Col>
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <StyledCheckBoxGuardian
              value={needGuardian}
              disabled={cannotUncheck}
              onChange={setNeedGuardian}>
              {t('need-guardian')}
            </StyledCheckBoxGuardian>
            {needGuardian && (
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={KeyForm.GuardianName}
                    rules={[
                      {
                        required: true,
                        message: t('please-enter-info') || ''
                      },
                      {
                        max: 100,
                        message:
                          t('name-not-100-chart', {
                            name: t('patient').toLowerCase()
                          }) || ''
                      }
                    ]}>
                    <Input
                      maxLength={100}
                      isCapitalizeName
                      title={t('guardian-name')}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={KeyForm.GuardianYearBirthday}
                    rules={[
                      {
                        validator: async (_, value) => {
                          const currentYear = new Date().getFullYear()
                          const guardianAge = currentYear - value
                          if (guardianAge < 18) {
                            return Promise.reject(
                              t('guardian-must-be-at-least-18')
                            )
                          }
                        }
                      }
                    ]}>
                    <Select
                      allowClear
                      showSearch
                      title={t('birthday')}
                      optionFilterProp="children"
                      options={getYearBirthdayOptions(120, month, date)}
                      notFoundContent={<></>}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={KeyForm.GuardianPhoneNumber}
                    validateTrigger={validateTrigger.phoneNumber}
                    rules={[
                      {
                        required: true,
                        message: t('please-enter-info') || ''
                      },
                      {
                        pattern: PHONE_NUMBER_REG,
                        message: t('format-number-error') || ''
                      }
                    ]}>
                    <Input
                      type="number"
                      title={t('phone-number')}
                      required
                      onBlur={() =>
                        setValidateTrigger({
                          ...validateTrigger,
                          phoneNumber: ['onBlur', 'onChange']
                        })
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        <Divider plain>{t('img').toUpperCase()}</Divider>
        <Row>
          <Col span={12}>
            <Form.Item name={KeyForm.AvatarUrl}>
              <UploadImageCrop aspect={4 / 4} title={t('avatar')} />
            </Form.Item>
          </Col>
          {!isEdit && (
            <>
              <Col span={24}>
                <StyledCheckBox
                  value={isCreateAccount}
                  onChange={setIsCreateAccount}>
                  {t('create-account-on-amaz')}
                </StyledCheckBox>
              </Col>
              {isCreateAccount && (
                <Col span={8} style={{ marginTop: 12 }}>
                  <Form.Item
                    name={KeyForm.Password}
                    rules={[
                      {
                        required: isCreateAccount,
                        message: t('please-enter-password') || ''
                      }
                    ]}>
                    <Input
                      required
                      id="add-patient-password"
                      type={passwordVisible ? 'text' : 'password'}
                      title="password"
                      placeholder={t('password')}
                      autoComplete="new-password"
                      suffix={
                        passwordVisible ? (
                          <EyeOutlined
                            onClick={() => handleSetPasswordVisible(false)}
                          />
                        ) : (
                          <EyeInvisibleOutlined
                            onClick={() => handleSetPasswordVisible(true)}
                          />
                        )
                      }
                    />
                  </Form.Item>
                </Col>
              )}
            </>
          )}
        </Row>
      </StyledForm>
      <FooterModal loading={loading} onSubmit={handleOnSubmit} />
    </Disabled>
  )
}

export default AddProfileModal
